import type { ProductAvailabilityConfig, ProductAvailabilityWindow } from "@workspace/api-client-react";
import {
  inicioDoDia,
  turnoCabeNasFaixas,
  type HorarioDosTurnos,
  type ModoEncomenda,
  type Turno,
} from "@workspace/pricing";
import { getStoreTimezone } from "@/lib/store-timezone";

export const DAY_OF_WEEK_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
export const DAY_OF_WEEK_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export function isAvailabilityConfigActive(config?: ProductAvailabilityConfig | null): boolean {
  if (!config || !config.enabled) return false;
  if (config.mode === "weekly") return Array.isArray(config.weekly) && config.weekly.some(d => d.ranges && d.ranges.length > 0);
  if (config.mode === "monthly") return !!config.monthly?.days?.length && !!config.monthly?.ranges?.length;
  if (config.mode === "specific_dates") return !!config.specificDates?.dates?.length && !!config.specificDates?.ranges?.length;
  return false;
}

function dateStr(y: number, m0: number, d: number): string {
  return `${y}-${String(m0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseDateStr(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export function getWindowsForDate(config: ProductAvailabilityConfig | null | undefined, dateStr: string): ProductAvailabilityWindow[] {
  if (!isAvailabilityConfigActive(config) || !config) return [];
  const dt = parseDateStr(dateStr);
  if (config.mode === "weekly") {
    const dow = dt.getDay();
    const day = config.weekly?.find(d => d.dayOfWeek === dow);
    return day?.ranges ?? [];
  }
  if (config.mode === "monthly") {
    const dom = dt.getDate();
    if (!config.monthly?.days?.includes(dom)) return [];
    return config.monthly.ranges ?? [];
  }
  if (config.mode === "specific_dates") {
    if (!config.specificDates?.dates?.includes(dateStr)) return [];
    return config.specificDates.ranges ?? [];
  }
  return [];
}

export function isDateAllowed(config: ProductAvailabilityConfig | null | undefined, dateStr: string): boolean {
  return getWindowsForDate(config, dateStr).length > 0;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function minutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const SLOT_INCREMENT = 30;

export function getTimeSlotsForWindows(windows: ProductAvailabilityWindow[], stepMinutes: number = SLOT_INCREMENT): string[] {
  const step = stepMinutes > 0 ? stepMinutes : SLOT_INCREMENT;
  const slots = new Set<string>();
  for (const w of windows) {
    const start = timeToMinutes(w.start);
    const end = timeToMinutes(w.end);
    if (end <= start) continue;
    let t = Math.ceil(start / step) * step;
    if (t < start) t = start;
    while (t <= end) {
      slots.add(minutesToTime(t));
      t += step;
    }
  }
  return Array.from(slots).sort();
}

export function getAvailableTimeSlotsForDate(config: ProductAvailabilityConfig | null | undefined, dateStr: string, stepMinutes?: number): string[] {
  return getTimeSlotsForWindows(getWindowsForDate(config, dateStr), stepMinutes);
}

export function computeNextAvailableDates(
  config: ProductAvailabilityConfig | null | undefined,
  minAdvanceDays: number,
  count: number,
  today: Date,
  horizonDays = 60,
): string[] {
  const result: string[] = [];
  if (!isAvailabilityConfigActive(config)) return result;
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() + Math.max(0, minAdvanceDays));
  for (let i = 0; i < horizonDays && result.length < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const ds = dateStr(d.getFullYear(), d.getMonth(), d.getDate());
    if (isDateAllowed(config, ds)) result.push(ds);
  }
  return result;
}

// Antecedência em HORAS (03/10/2026, pedido do Pedro): o primeiro horário que dá para agendar é agora + H no
// relógio da loja — pediu 23h com 24 h, só a partir de 23h de amanhã. A validação do servidor faz a mesma conta
// (routes/catalog/orders.ts). Em dias o prazo conta o calendário (computeNextAvailableDates).
export function primeiroMomentoEncomenda(horas: number, agora: Date = new Date()): { data: string; hora: string } {
  const alvo = new Date(agora.getTime() + horas * 3600_000);
  const partes = new Intl.DateTimeFormat("en-CA", {
    timeZone: getStoreTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(alvo);
  const v = (t: string) => partes.find((p) => p.type === t)?.value ?? "00";
  return { data: `${v("year")}-${v("month")}-${v("day")}`, hora: `${v("hour") === "24" ? "00" : v("hour")}:${v("minute")}` };
}

// Próximas datas com dia/horário disponível, nas duas formas de antecedência. Em horas, o primeiro dia só conta
// se ainda tiver faixa depois do primeiro horário permitido.
// Encomenda por turno ou só pela data (12/10/2026): o que o produto pede e o horário dos turnos da loja.
export type RegraDaEncomenda = { modo: ModoEncomenda; turnos: readonly Turno[]; horarios: HorarioDosTurnos };

type Prazo = { data: string; hora: string } | null;

// Turnos que o produto oferece no dia: os ligados no produto E na loja, que a "Disponibilidade limitada" do dia cobre
// e que começam depois do prazo em horas (a antecedência conta até o começo do turno). Ocupação e capacidade ficam de
// fora.
export function turnosDoDia(
  config: ProductAvailabilityConfig | null | undefined,
  ds: string,
  regra: RegraDaEncomenda,
  primeiro: Prazo,
): Turno[] {
  const faixas = isAvailabilityConfigActive(config) ? getWindowsForDate(config, ds) : null;
  return regra.turnos.filter(
    (t) =>
      regra.horarios[t].ligado &&
      (faixas === null || turnoCabeNasFaixas(regra.horarios[t], faixas)) &&
      (!primeiro || `${ds}T${regra.horarios[t].start}` >= `${primeiro.data}T${primeiro.hora}`),
  );
}

// "Só a data": o dia serve se o produto é feito nele e, com prazo em horas, se o dia começa depois do prazo.
export function diaServeSoData(config: ProductAvailabilityConfig | null | undefined, ds: string, primeiro: Prazo): boolean {
  const faixas = isAvailabilityConfigActive(config) ? getWindowsForDate(config, ds) : null;
  if (faixas !== null && faixas.length === 0) return false;
  return !primeiro || `${ds}T${inicioDoDia(faixas)}` >= `${primeiro.data}T${primeiro.hora}`;
}

// `ate` e `de`: «Aceitar encomendas» de/até do produto (26/10/2026): dia fora da janela não entra. Com o início no
// futuro, a busca começa nele (senão os 60 dias acabavam antes).
export function proximasDatasEncomenda(
  config: ProductAvailabilityConfig | null | undefined,
  minAdvanceDays: number,
  minAdvanceHours: number | null | undefined,
  count: number,
  agora: Date = new Date(),
  horizonDays = 60,
  regra?: RegraDaEncomenda,
  ate?: string | null,
  de?: string | null,
): string[] {
  const primeiro = minAdvanceHours ? primeiroMomentoEncomenda(minAdvanceHours, agora) : null;
  let candidatos = primeiro
    ? computeNextAvailableDates(config, 0, count * 4 + 10, parseDateStr(primeiro.data), horizonDays)
    : computeNextAvailableDates(config, minAdvanceDays, count * 4 + 10, agora, horizonDays);
  if (de) {
    const inicioDoPrazo = primeiro ? parseDateStr(primeiro.data) : new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + Math.max(0, minAdvanceDays));
    if (parseDateStr(de) > inicioDoPrazo) candidatos = computeNextAvailableDates(config, 0, count * 4 + 10, parseDateStr(de), horizonDays);
  }
  const serve = (d: string): boolean => {
    if (!regra || regra.modo === "horario") {
      return !primeiro || d !== primeiro.data || getWindowsForDate(config, d).some((w) => w.end >= primeiro.hora);
    }
    if (regra.modo === "turno") return turnosDoDia(config, d, regra, primeiro).length > 0;
    return diaServeSoData(config, d, primeiro);
  };
  return candidatos.filter((d) => (!ate || d <= ate) && (!de || d >= de)).filter(serve).slice(0, count);
}

export function formatAvailabilityDateLabel(dateStr: string): string {
  const dt = parseDateStr(dateStr);
  const dayName = DAY_OF_WEEK_SHORT[dt.getDay()];
  const day = dt.getDate();
  const month = dt.getMonth() + 1;
  return `${dayName} ${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`;
}

export function formatAvailabilityDateLong(dateStr: string): string {
  const months = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const dt = parseDateStr(dateStr);
  return `${DAY_OF_WEEK_LABELS[dt.getDay()]}, ${dt.getDate()} de ${months[dt.getMonth()]}`;
}

export function summarizeAvailabilityConfig(config: ProductAvailabilityConfig | null | undefined): string {
  if (!isAvailabilityConfigActive(config) || !config) return "";
  if (config.mode === "weekly") {
    const days = (config.weekly ?? []).filter(d => d.ranges?.length).map(d => DAY_OF_WEEK_SHORT[d.dayOfWeek]).join(", ");
    return days ? `${days}` : "";
  }
  if (config.mode === "monthly") {
    const days = (config.monthly?.days ?? []).slice().sort((a, b) => a - b).join(", ");
    return `Dias ${days} do mês`;
  }
  if (config.mode === "specific_dates") {
    const total = config.specificDates?.dates?.length ?? 0;
    return `${total} ${total === 1 ? "data" : "datas"} específicas`;
  }
  return "";
}
